import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { i18n } from '@lingui/core';
import { Trans, msg, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import PropTypes from 'prop-types';
import { Grid } from 'semantic-ui-react';

import {
  entityLabelFormatterSelector,
  ontologiesSelector,
} from 'selectors/entities';
import {
  aggregatesSelector,
  viewFacetAggregatesLoadingSelector,
} from 'selectors/view';

import DistributionVisualization from 'components/customer/visualization/DistributionVisualization';
import OverviewTimeSeriesVisualizations from 'components/customer/visualization/OverviewTimeSeriesVisualizations';
import { NormalSpacePaddedRow, SmallSpacePaddedGrid } from 'components/ui/Grid';
import { exportElementAsImage } from 'components/ui/button/export-as/ExportAsImage';
import {
  ChartMenu,
  ChartSegment,
  HeaderContainer,
  TitleSpan,
} from 'components/ui/dashboard/DashboardElement';
import InTextDropdown from 'components/ui/inputs/InTextDropdown';

import { logEvent } from 'utils/analytics';
import commonPropTypes from 'utils/commonPropTypes';

import * as svars from 'assets/style/variables';

import ViewFacetDashboardKpis from '../ViewFacetDashboardKpis';
import ConceptPrioritization from './ConceptPrioritization';

const generalDistributionField = (field) =>
  field === 'db_ontology' ? 'ontology' : field;

const DISTRIBUTIONS_DISPLAY_OPTIONS = [
  {
    key: 'source',
    value: 'source',
    i18nText: msg({ id: 'source' }),
  },
  {
    key: 'ontology',
    value: 'db_ontology',
    i18nText: msg({ id: 'ontology' }),
  },
];

function V2Dashboard({
  viewFacet,
  maybeFetchTimeSeriesVisualizationsAggregates,
  hidden,
}) {
  useLingui();
  const [displayDistributionOption, setDisplayDistributionOption] =
    useState('source');

  const entityLabelFormatter = useSelector(entityLabelFormatterSelector);
  const { timeSeries, generalDistributions } = useSelector(aggregatesSelector);
  const ontologies = useSelector(ontologiesSelector());
  const aggregatesLoading = useSelector(viewFacetAggregatesLoadingSelector);

  useEffect(() => {
    maybeFetchTimeSeriesVisualizationsAggregates(viewFacet.id, 'general_dist');
    maybeFetchTimeSeriesVisualizationsAggregates(viewFacet.id, 'concept_kpi');
    maybeFetchTimeSeriesVisualizationsAggregates(viewFacet.id, 'ts');
    maybeFetchTimeSeriesVisualizationsAggregates(
      viewFacet.id,
      'top_bottom_concept'
    );
    maybeFetchTimeSeriesVisualizationsAggregates(
      viewFacet.id,
      'competition_kpi'
    );
  }, []);

  const onSetDisplayDistributionOption = useCallback(
    (_, { value }) => setDisplayDistributionOption(value),
    []
  );

  const getExportName = () => {
    const distributionDisplayWording = DISTRIBUTIONS_DISPLAY_OPTIONS.find(
      (display) => display.value === displayDistributionOption
    ).i18nText;
    return `${t({ id: 'distribution-by' })} ${i18n._(
      distributionDisplayWording
    )}`;
  };

  const chartId = `distribution-visualization-${viewFacet.id}`;
  const legendFormatter = (item) =>
    entityLabelFormatter(displayDistributionOption, item);
  const exportChartAsImage = useCallback(() => {
    const exportFileName = getExportName();
    exportElementAsImage(document, chartId, exportFileName, () => {
      logEvent({
        category: 'Customer - Analyze',
        action: 'Export image',
        label: exportFileName,
      });
    });
  }, [chartId, getExportName]);
  if (hidden || !Object.keys(ontologies).length) return null;
  return (
    <SmallSpacePaddedGrid>
      <NormalSpacePaddedRow style={{ width: '100%' }}>
        <ViewFacetDashboardKpis viewFacet={viewFacet} />
      </NormalSpacePaddedRow>

      <NormalSpacePaddedRow style={{ width: '100%', paddingTop: 0 }}>
        <OverviewTimeSeriesVisualizations
          viewFacet={viewFacet}
          loading={aggregatesLoading}
          data={timeSeries ? timeSeries.monthly : undefined}
          chartId={`ts-general-${viewFacet.id}`}
          useReviewScore={viewFacet.use_review_score}
        />

        <Grid.Column width={4} stretched style={{ padding: 0 }}>
          <ChartSegment>
            <HeaderContainer>
              <TitleSpan>
                <Trans id="distribution-by" />{' '}
                <InTextDropdown
                  placeholder={t({ id: 'select-an-axis' })}
                  options={DISTRIBUTIONS_DISPLAY_OPTIONS}
                  data-testid="bo-dropdown-chart-menu-button"
                  onChange={onSetDisplayDistributionOption}
                  value={displayDistributionOption}
                  style={{ width: 'auto' }}
                  fluid
                />
              </TitleSpan>
              <ChartMenu exportAsImage={exportChartAsImage} />
            </HeaderContainer>
            <DistributionVisualization
              height={svars.chartHeight}
              data={
                generalDistributions?.[
                  generalDistributionField(displayDistributionOption)
                ] || undefined
              }
              cellKey={displayDistributionOption}
              key={`dist-${displayDistributionOption}`}
              indicatorKey="n_chunks"
              legendFormatter={legendFormatter}
              tooltipExtraFieldsKeys={['average_sentiment', 'average_score']}
              tooltipHeaderField={{
                key: displayDistributionOption,
                isEntity: true,
                formatter: legendFormatter,
              }}
              loading={aggregatesLoading}
              othersCellLabel={(nSource) => t`${nSource} extra sources`}
              chartId={chartId}
            />
          </ChartSegment>
        </Grid.Column>
      </NormalSpacePaddedRow>
      <ConceptPrioritization
        loading={aggregatesLoading}
        viewFacet={viewFacet}
      />
    </SmallSpacePaddedGrid>
  );
}

V2Dashboard.propTypes = {
  hidden: PropTypes.bool,
  viewFacet: commonPropTypes.viewFacet.isRequired,
  maybeFetchTimeSeriesVisualizationsAggregates: PropTypes.func.isRequired,
};

V2Dashboard.defaultProps = {
  hidden: false,
};

export default V2Dashboard;
